import {
  AppstoreOutlined, DeleteOutlined, EditOutlined,
  EyeOutlined, FilterOutlined, InboxOutlined, PlusOutlined,
  ProductOutlined,
  ReloadOutlined, SearchOutlined,
  SyncOutlined, UndoOutlined,
} from '@ant-design/icons';
import {
  Button, Card, Descriptions, Input, Modal,
  Popconfirm, Select, Space, Tag, Tooltip, Typography, message,
} from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';
import {FaCheck} from 'react-icons/fa';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../../CommonComponents/Cards';
import {TableData} from '../../../../CommonComponents/TableData';
import {
  RqDeleteFinalProduct,
  RqGetAllFinalProducts,
  RqGetDeletedFinalProducts,
  RqRestoreFinalProduct,
} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';

import {AddProduct} from './AddProduct';
import {EditProduct} from './EditProduct';

const {Text} = Typography;

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

// ─── Product Detail Modal ─────────────────────────────────────────────────────
const ProductDetailModal = ({product, onClose}) => (
  <Modal
    open={!!product}
    onCancel={onClose}
    footer={null}
    title={(
      <span className={'text-primary-700 font-bold text-base'}>
        {product?.productName}
      </span>
    )}
    width={560}
    destroyOnClose
  >
    {product && (
      <>
        <Descriptions column={1} bordered size={'small'} className={'mb-6 mt-4'}>
          <Descriptions.Item label={'Brand'}>{product.brand}</Descriptions.Item>
          <Descriptions.Item label={'Category'}>{product.category}</Descriptions.Item>
          <Descriptions.Item label={'HSN Code'}>{product.hsnCode || '—'}</Descriptions.Item>
          <Descriptions.Item label={'Quantity'}>{product.quantity}</Descriptions.Item>
          <Descriptions.Item label={'Status'}>
            <Tag color={product.status === 'ACTIVE' ? 'green' : 'red'}>{product.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={'Tax Breakdown'}>
            <Space size={4}>
              <Tag color={'blue'}>CGST: {product.tax?.cgst ?? product.cgst ?? 0}%</Tag>
              <Tag color={'blue'}>SGST: {product.tax?.sgst ?? product.sgst ?? 0}%</Tag>
              <Tag color={'blue'}>IGST: {product.tax?.igst ?? product.igst ?? 0}%</Tag>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={'Description'}>{product.description || '—'}</Descriptions.Item>
          <Descriptions.Item label={'Created On'}>
            {new Date(product.createdOn).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>

        {product.ingredients?.length > 0 && (
          <>
            <p className={'font-semibold text-primary-700 mb-2'}>Ingredients</p>
            <TableData
              size={'small'}
              dataSource={product.ingredients}
              rowKey={'productId'}
              columns={[
                {title: 'Name', dataIndex: 'productName'},
                {title: 'Qty',  dataIndex: 'quantity'},
                {title: 'Unit', dataIndex: 'unit'},
              ]}
              className={'mb-6'}
            />
          </>
        )}

        {product.lots?.length > 0 && (
          <>
            <p className={'font-semibold text-primary-700 mb-2'}>Lots</p>
            <TableData
              size={'small'}
              dataSource={product.lots}
              rowKey={'lotNumber'}
              columns={[
                {title: 'Lot #',           dataIndex: 'lotNumber'},
                {
                  title:     'Production Date',
                  dataIndex: 'productionDate',
                  render:    d => new Date(d).toLocaleDateString(),
                },
                {title: 'Qty', dataIndex: 'quantity'},
              ]}
            />
          </>
        )}
      </>
    )}
  </Modal>
);

ProductDetailModal.propTypes = {
  product: PropTypes.shape({
    productName:  PropTypes.string,
    brand:        PropTypes.string,
    category:     PropTypes.string,
    hsnCode:      PropTypes.string,
    quantity:     PropTypes.number,
    status:       PropTypes.string,
    description:  PropTypes.string,
    createdOn:    PropTypes.string,
    cgst:         PropTypes.number,
    sgst:         PropTypes.number,
    igst:         PropTypes.number,
    tax:          PropTypes.shape({
      cgst: PropTypes.number,
      sgst: PropTypes.number,
      igst: PropTypes.number,
    }),
    ingredients:  PropTypes.arrayOf(PropTypes.shape({
      productId:   PropTypes.string,
      productName: PropTypes.string,
      quantity:    PropTypes.number,
      unit:        PropTypes.string,
    })),
    lots: PropTypes.arrayOf(PropTypes.shape({
      lotNumber:      PropTypes.string,
      productionDate: PropTypes.string,
      quantity:       PropTypes.number,
    })),
  }),
  onClose: PropTypes.func.isRequired,
};

// ─── Main Screen ──────────────────────────────────────────────────────────────
const FinalProductScreen = () => {
  const [addModalOpen,     setAddModalOpen]     = useState(false);
  const [editProduct,      setEditProduct]      = useState(null);
  const [detailProduct,    setDetailProduct]    = useState(null);
  const [deletedModalOpen, setDeletedModalOpen] = useState(false);

  // ── Local client-side filter state ──
  const [searchText,     setSearchText]     = useState('');
  const [statusFilter,   setStatusFilter]   = useState([]);

  const auth  = useSelector(selectAuth);
  const token = auth?.tokenData;

  const {data: allProducts = [], isFetching: allLoading, refetch} = RqGetAllFinalProducts(token);
  const {data: deletedProducts = []} = RqGetDeletedFinalProducts(token);
  const deleteProduct = RqDeleteFinalProduct();

  // ── Client-side filtering (instant, no extra API call needed) ────────────
  const products = useMemo(() => {
    let list = [...allProducts].sort(
      (a, b) => new Date(b.createdOn ?? 0) - new Date(a.createdOn ?? 0),
    );

    // Search by product name (case-insensitive substring)
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(p => p.productName?.toLowerCase().includes(q));
    }

    // Filter by status (if any selected)
    if (statusFilter.length > 0) {
      list = list.filter(p => statusFilter.includes(p.status));
    }

    return list;
  }, [allProducts, searchText, statusFilter]);

  const totalQty    = useMemo(() => products.reduce((s, p) => s + (p.quantity || 0), 0), [products]);
  const activeCount = useMemo(() => products.filter(p => p.status === 'ACTIVE').length,  [products]);

  const handleDelete = async id => {
    if (!id) {
      message.error('Product ID missing');
      return;
    }
    try {
      await deleteProduct.mutateAsync({id, token});
      message.success('Product deleted');
    } catch (err) {
      console.error('Delete product error:', err);
      message.error(err?.response?.data?.message || err?.message || 'Delete failed');
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter([]);
  };

  const hasActiveFilter = searchText.trim() !== '' || statusFilter.length > 0;

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title:  'Sr. No.',
      key:    'srno',
      width:  70,
      render: (_, __, index) => index + 1,
    },
    {
      title:     'Product Name',
      dataIndex: 'productName',
      sorter:    (a, b) => a.productName.localeCompare(b.productName),
      render:    (name, rec) => (
        <button
          onClick={() => setDetailProduct(rec)}
          className={'hover:text-primary-800 font-semibold text-base text-left hover:underline bg-transparent border-0 cursor-pointer p-0'}
        >
          {name}
        </button>
      ),
    },
    {
      title:    'Brand',
      dataIndex: 'brand',
      filters:  [...new Set(allProducts.map(p => p.brand))].map(b => ({text: b, value: b})),
      onFilter: (v, r) => r.brand === v,
    },
    {title: 'Category', dataIndex: 'category'},
    {
      title:     'CGST',
      dataIndex: ['tax', 'cgst'],
      render:    value => value !== undefined ? `${value}%` : <span className={'text-gray-400'}>—</span>,
    },
    {
      title:     'SGST',
      dataIndex: ['tax', 'sgst'],
      render:    value => value !== undefined ? `${value}%` : <span className={'text-gray-400'}>—</span>,
    },
    {
      title:     'IGST',
      dataIndex: ['tax', 'igst'],
      render:    value => value !== undefined ? `${value}%` : <span className={'text-gray-400'}>—</span>,
    },
    {
      title:     'HSN Code',
      dataIndex: 'hsnCode',
      render:    v => v || <span className={'text-gray-400'}>—</span>,
    },
    {
      title:     'Quantity',
      dataIndex: 'quantity',
      sorter:    (a, b) => a.quantity - b.quantity,
      render:    qty => <p className={'text-base'}>{qty || '—'}</p>,
    },
    {
      title:     'Status',
      dataIndex: 'status',
      render:    s => <Tag color={s === 'ACTIVE' ? 'blue' : 'default'} className={'font-medium'}>{s}</Tag>,
    },
    {
      title:            'Created On',
      dataIndex:        'createdOn',
      defaultSortOrder: 'descend',
      sorter:           (a, b) => new Date(a.createdOn) - new Date(b.createdOn),
      render:           d => new Date(d).toLocaleDateString(),
    },
    {
      title:  'Actions',
      key:    'actions',
      width:  120,
      render: (_, rec) => (
        <Space size={4}>
          <Tooltip title={'View Details'}>
            <Button
              size={'small'}
              icon={<EyeOutlined />}
              onClick={() => setDetailProduct(rec)}
              className={'text-primary-600 border-primary-200 hover:border-primary-400'}
            />
          </Tooltip>
          <Tooltip title={'Edit'}>
            <Button
              size={'small'}
              icon={<EditOutlined />}
              onClick={() => setEditProduct(rec)}
              className={'text-primary-600 border-primary-200 hover:border-primary-400'}
            />
          </Tooltip>
          <Tooltip title={'Delete'}>
            <Popconfirm
              title={'Delete this product?'}
              onConfirm={() => handleDelete(rec.id || rec._id || rec.productId)}
              okButtonProps={{danger: true}}
              okText={'Delete'}
            >
              <Button size={'small'} icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={'min-h-screen bg-white md:p-6 p-2'}>

      {/* Header */}
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}
          >
            <ProductOutlined style={{fontSize: 22}} />
          </div>
          <Title level={3} className={'header !mb-0'}>Final Products</Title>
        </div>

        <Space>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeletedModalOpen(true)}
          >
            Deleted Products ({deletedProducts.length})
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={allLoading}
            className={'btn-secondary'}
          >
            Refresh
          </Button>
          <Button
            type={'primary'}
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
            className={'btn-primary'}
          >
            Register Product
          </Button>
        </Space>
      </div>

      {/* Stat Cards */}
      <div className={'grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'}>
        <StatCard icon={<AppstoreOutlined />} label={'Total Products'}  value={products.length} colorClass={'bg-white'}      />
        <StatCard icon={<FaCheck />}          label={'Active Products'} value={activeCount}      colorClass={'bg-primary-50'} />
        <StatCard icon={<InboxOutlined />}    label={'Total Quantity'}  value={totalQty}         colorClass={'bg-white'}      />
      </div>

      {/* ── Filter Bar ── */}
      <div className={'mb-5 bg-white border border-gray-100 rounded-lg p-4 shadow-sm'}>
        <div className={'flex items-center gap-2 mb-3'}>
          <FilterOutlined className={'text-primary-500'} />
          <span className={'font-semibold text-gray-700 text-sm'}>Filters</span>
          {hasActiveFilter && (
            <Tag color={'blue'} className={'ml-1 text-xs'}>
              {products.length} result{products.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </div>

        <div className={'flex flex-wrap gap-3 items-center'}>
          {/* Search */}
          <Input
            prefix={<SearchOutlined className={'text-gray-400'} />}
            placeholder={'Search by product name…'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{width: 280}}
          />

          {/* Status multi-select */}
          <Select
            mode={'multiple'}
            placeholder={'Filter by status'}
            value={statusFilter}
            onChange={val => setStatusFilter(val)}
            style={{minWidth: 200}}
            options={STATUS_OPTIONS.map(s => ({
              value: s,
              label: (
                <Tag color={s === 'ACTIVE' ? 'blue' : 'default'} className={'text-xs my-0'}>
                  {s}
                </Tag>
              ),
            }))}
            allowClear
            onClear={() => setStatusFilter([])}
          />

          {/* Reset — only shown when something is active */}
          {hasActiveFilter && (
            <Button icon={<SyncOutlined />} onClick={handleClearFilters}>
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <Card className={'rounded-lg border border-primary-100 shadow-sm'} Styles={{padding: 0}}>
        <TableData
          dataSource={products}
          columns={columns}
          rowKey={rec => rec.id || rec._id || rec.productId || rec.productName}
          loading={allLoading}
          size={'middle'}
          scroll={{x: 900}}
          rowClassName={'hover:bg-primary-50 transition-colors'}
        />
      </Card>

      <AddProduct
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        token={token}
      />

      <EditProduct
        open={!!editProduct}
        onClose={() => setEditProduct(null)}
        product={editProduct}
        token={token}
      />

      <ProductDetailModal
        product={detailProduct}
        onClose={() => setDetailProduct(null)}
      />

      <DeletedProductsModal
        open={deletedModalOpen}
        onClose={() => setDeletedModalOpen(false)}
        token={token}
      />
    </div>
  );
};

// ─── Deleted Products Modal ──────────────────────────────────────────────────
const DeletedProductsModal = ({open, onClose, token}) => {
  const {data: deletedProducts = [], isLoading, refetch} = RqGetDeletedFinalProducts(token, {enabled: open});
  const restoreProduct = RqRestoreFinalProduct();

  const handleRestore = async id => {
    try {
      await restoreProduct.mutateAsync({id, token});
      message.success('Final product restored successfully');
      refetch();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Restore failed');
    }
  };

  const columns = [
    {title: 'Sr. No.', key: 'srno', width: 65, render: (_, __, index) => index + 1},
    {title: 'Product Name', dataIndex: 'productName', render: v => <Text strong className={'text-blue-800'}>{v}</Text>},
    {title: 'Brand', dataIndex: 'brand'},
    {title: 'Category', dataIndex: 'category'},
    {title: 'Qty', dataIndex: 'quantity'},
    {title: 'Status', dataIndex: 'status', render: () => <Tag color={'red'}>DELETED</Tag>},
    {
      title: 'Action',
      key: 'action',
      render: (_, rec) => (
        <Popconfirm
          title={'Restore this product?'}
          onConfirm={() => handleRestore(rec.id || rec._id)}
          okText={'Restore'}
        >
          <Button type={'primary'} size={'small'} icon={<UndoOutlined />}>
            Restore
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={'Deleted Final Products (30-Day Recovery)'}
      width={750}
      destroyOnClose
    >
      <TableData
        loading={isLoading}
        dataSource={deletedProducts}
        columns={columns}
        rowKey={rec => rec.id || rec._id}
        size={'small'}
        pagination={{pageSize: 5}}
      />
    </Modal>
  );
};

DeletedProductsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string,
};

export default FinalProductScreen;
